import { useState } from 'react';
import { computeOverall, DEFAULT_WEIGHTS, SUB_SCORE_KEYS, type KpiWeights } from '@rolefit/domain/fit';
import { Badge, Button, Dialog, DrawerContent, DialogClose, Textarea, cn } from '@rolefit/ui';
import { queries, type PackagePayload } from '../lib/queries.js';
import { useAsync } from '../lib/useAsync.js';
import { fitTone } from '../lib/fit.js';

// Human-readable reasons for each guardrail flag, shown when a package is flagged.
const FLAG_LABELS: Record<string, string> = {
  em_dash: 'Contains an em-dash (—), which is an ATS and AI-tell red flag',
  banned_phrases: 'Uses generic filler phrases',
  no_fabrication: 'A claim could not be traced to a confirmed fact',
  honest_gaps: 'Overstates a gap instead of representing it honestly',
  ats_format: 'Uses formatting an ATS may not parse (tables or images)',
  entailment: 'A claim is not supported by the confirmed profile',
};

const flagDetail = (detail: unknown): string | null => {
  if (Array.isArray(detail) && detail.length > 0) return detail.map(String).join(', ');
  return null;
};

// The generated package: three artifacts (CV, fit notes, cover) behind tabs,
// plus the guardrail reasons when a package is flagged. All of this is already
// fetched; the viewer just renders it.
function PackageSection({
  pkg,
  roleId,
  busy,
  onDownload,
  onRebuilt,
}: {
  pkg: PackagePayload;
  roleId: string;
  busy: boolean;
  onDownload: () => void;
  onRebuilt: () => void;
}) {
  const artifacts = [
    { key: 'cv', label: 'CV', md: pkg.cv_md },
    { key: 'notes', label: 'Fit notes', md: pkg.notes_md },
    { key: 'cover', label: 'Cover note', md: pkg.cover_md },
  ].filter((a) => a.md);
  const [tab, setTab] = useState(artifacts[0]?.key ?? 'cv');
  const active = artifacts.find((a) => a.key === tab) ?? artifacts[0];
  const failed = (pkg.guardrail_report?.checks ?? []).filter((c) => !c.passed);

  // Inline CV edit + rebuild (no model call): edit the Markdown, rebuild the PDF.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(pkg.cv_md ?? '');
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildError, setRebuildError] = useState<string | null>(null);

  async function rebuild() {
    setRebuilding(true);
    setRebuildError(null);
    try {
      await queries.rebuildPackage(roleId, { cv_md: draft });
      setEditing(false);
      onRebuilt();
    } catch (e) {
      setRebuildError(e instanceof Error ? e.message : 'Rebuild failed');
    } finally {
      setRebuilding(false);
    }
  }

  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-medium">Package</h3>
        <Badge variant={pkg.status === 'ready' ? 'success' : pkg.status === 'flagged' ? 'warning' : 'secondary'}>
          {pkg.status}
        </Badge>
        {pkg.has_pdf && (
          <Button size="sm" variant="outline" className="ml-auto" disabled={busy} onClick={onDownload}>
            Download PDF
          </Button>
        )}
      </div>

      {failed.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
          <p className="mb-1 font-medium text-amber-700 dark:text-amber-300">
            This package was flagged. Review before sending:
          </p>
          <ul className="space-y-1">
            {failed.map((c) => {
              const d = flagDetail(c.detail);
              return (
                <li key={c.name} className="text-muted-foreground">
                  • {FLAG_LABELS[c.name] ?? c.name}
                  {d && <span className="text-foreground"> ({d})</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {artifacts.length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div className="inline-flex rounded-md border p-0.5 text-xs">
              {artifacts.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  disabled={editing}
                  className={cn(
                    'rounded px-3 py-1 disabled:opacity-50',
                    tab === a.key ? 'bg-secondary font-medium' : 'text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setTab(a.key)}
                >
                  {a.label}
                </button>
              ))}
            </div>
            {tab === 'cv' &&
              (editing ? (
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="ghost" disabled={rebuilding} onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" disabled={rebuilding} onClick={rebuild}>
                    {rebuilding ? 'Rebuilding…' : 'Rebuild PDF'}
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  disabled={busy}
                  onClick={() => {
                    setDraft(pkg.cv_md ?? '');
                    setEditing(true);
                  }}
                >
                  Edit CV
                </Button>
              ))}
          </div>
          {rebuildError && <p className="mb-2 text-xs text-destructive">{rebuildError}</p>}
          {tab === 'cv' && editing ? (
            <Textarea
              rows={14}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="font-mono text-xs"
            />
          ) : (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs">
              {active?.md}
            </pre>
          )}
        </>
      )}
    </section>
  );
}

export function RoleDrawer({
  roleId,
  weights,
  onClose,
  onChanged,
}: {
  roleId: string;
  weights: KpiWeights;
  onClose: () => void;
  onChanged: () => void;
}) {
  const role = useAsync(() => queries.getRole(roleId), [roleId]);
  const pkg = useAsync<PackagePayload | null>(() => queries.getPackage(roleId), [roleId]);
  const mail = useAsync(() => queries.getRoleMessages(roleId), [roleId]);
  const [busy, setBusy] = useState<null | 'score' | 'package' | 'track' | 'pdf' | 'delete'>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function remove() {
    setBusy('delete');
    setError(null);
    try {
      await queries.deleteRole(roleId);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setBusy(null);
    }
  }

  async function downloadPdf() {
    setBusy('pdf');
    setError(null);
    try {
      const blob = await queries.downloadPackagePdf(roleId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cv.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setBusy(null);
    }
  }

  async function run(kind: 'score' | 'package' | 'track', fn: () => Promise<unknown>) {
    setBusy(kind);
    setError(null);
    try {
      await fn();
      role.refetch();
      pkg.refetch();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(null);
    }
  }

  const overall = role.data ? computeOverall(role.data.sub_scores, weights) : 0;
  const tone = fitTone(overall);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DrawerContent aria-describedby={undefined}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">{role.data?.title ?? 'Role'}</h2>
            <p className="text-sm text-muted-foreground">{role.data?.company}</p>
          </div>
          {role.data?.scored && (
            <span className={`rounded-md px-2 py-1 text-lg font-semibold tabular-nums ${tone.chip}`}>
              {overall.toFixed(1)}
            </span>
          )}
        </div>

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" disabled={busy !== null} onClick={() => run('score', () => queries.scoreRole(roleId))}>
            {busy === 'score' ? 'Scoring…' : role.data?.scored ? 'Re-score' : 'Score'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy !== null}
            onClick={() => run('package', () => queries.generatePackage(roleId))}
          >
            {busy === 'package' ? 'Generating…' : 'Generate package'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => run('track', () => queries.createApplication(roleId))}
          >
            Track
          </Button>

          {confirmingDelete ? (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Delete this role?</span>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={busy !== null}
                onClick={remove}
              >
                {busy === 'delete' ? 'Deleting…' : 'Confirm'}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-destructive"
              disabled={busy !== null}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </Button>
          )}
        </div>

        {role.data?.scored && (
          <section className="mt-6">
            <h3 className="mb-2 text-sm font-medium">Fit sub-scores</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {SUB_SCORE_KEYS.map((key) => {
                const v = role.data?.sub_scores[key];
                return (
                  <div key={key} className="flex items-center justify-between rounded-md border px-3 py-1.5">
                    <span className="capitalize text-muted-foreground">{key.replace('_', ' ')}</span>
                    <span className="tabular-nums">{v == null ? '—' : v}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {role.data?.evidence && role.data.evidence.length > 0 && (
          <section className="mt-6">
            <h3 className="mb-2 text-sm font-medium">Evidence</h3>
            <ul className="space-y-2 text-sm">
              {role.data.evidence.map((e, i) => (
                <li key={i} className="rounded-md border p-3">
                  <div className="font-medium">{e.requirement}</div>
                  <div className="text-muted-foreground">{e.evidence}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {pkg.data && (
          <PackageSection
            pkg={pkg.data}
            roleId={roleId}
            busy={busy !== null}
            onDownload={downloadPdf}
            onRebuilt={() => {
              pkg.refetch();
              onChanged();
            }}
          />
        )}

        {(mail.data?.messages.length ?? 0) > 0 && (
          <section className="mt-6">
            <h3 className="mb-2 text-sm font-medium">Correspondence</h3>
            <ul className="space-y-2 text-sm">
              {mail.data!.messages.map((m, i) => (
                <li key={i} className="rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{m.subject ?? '(no subject)'}</span>
                    {m.email_type && <Badge variant="outline">{m.email_type.replace('_', ' ')}</Badge>}
                    {m.status === 'accepted' && <Badge variant="success">applied</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {m.from}
                    {m.received_ts ? ` · ${new Date(m.received_ts).toLocaleDateString()}` : ''}
                  </div>
                  {m.snippet && <div className="mt-1 text-xs text-muted-foreground">{m.snippet}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-6">
          <h3 className="mb-2 text-sm font-medium">Job description</h3>
          <p className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md border p-3 text-sm text-muted-foreground">
            {role.data?.jd_text}
          </p>
        </section>

        <div className="mt-6">
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Close
            </Button>
          </DialogClose>
        </div>
      </DrawerContent>
    </Dialog>
  );
}

export const _defaultWeights: KpiWeights = DEFAULT_WEIGHTS;
